import { useCallback, useMemo } from 'react'
import useAuthContext from 'contexts/Auth/useAuthContext'
import siteConfig from 'config'

const DAILY_FREE_LIMIT = 1
const STORAGE_KEY = 'explain_usage'

interface ExplainUsage {
  date: string
  count: number
}

function getUsageToday(): ExplainUsage {
  const today = new Date().toISOString().slice(0, 10)
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const usage: ExplainUsage = JSON.parse(raw)
      if (usage.date === today) return usage
    }
  } catch {}
  return { date: today, count: 0 }
}

function incrementUsage(): void {
  const usage = getUsageToday()
  usage.count++
  localStorage.setItem(STORAGE_KEY, JSON.stringify(usage))
}

type ExplainGateResult =
  | { allowed: true; freeUsesLeft: number | null; onUse: () => void }
  | { allowed: false; reason: 'login'; freeUsesLeft: null; onUse: () => void }
  | { allowed: false; reason: 'limit'; freeUsesLeft: 0; onUse: () => void }

const useExplainGate = (): ExplainGateResult => {
  const { user, profile, beginSignInWithEmail } = useAuthContext()

  const purchased = profile?.explainPurchased === true
  const usage = getUsageToday()

  const onUse = useCallback(() => {
    if (!purchased) incrementUsage()
  }, [purchased])

  return useMemo(() => {
    if (!user) {
      return { allowed: false, reason: 'login' as const, freeUsesLeft: null, onUse: beginSignInWithEmail }
    }

    if (purchased) {
      return { allowed: true, freeUsesLeft: null, onUse }
    }

    const remaining = DAILY_FREE_LIMIT - usage.count
    if (remaining <= 0) {
      return { allowed: false, reason: 'limit' as const, freeUsesLeft: 0, onUse }
    }

    return { allowed: true, freeUsesLeft: remaining, onUse }
  }, [user, purchased, usage.count, onUse, beginSignInWithEmail])
}

export const getExplainCheckoutUrl = (userId: string): string => {
  const { checkoutBaseUrl, explainProductId } = siteConfig.payments
  if (!explainProductId) return ''
  return `${checkoutBaseUrl}/${userId}/${explainProductId}`
}

export default useExplainGate
